/**
 * TabStrip — the generic, kind-agnostic strip extracted from TabbedTerminal
 * (docs/tab-management.md Part 3 §6). Owns the presentation-level behaviors
 * every tab kind shares:
 *
 *   - Chrome-style equal-width chips: every tab is always visible; chips share
 *     the row width (flex basis 200px, min 40px) and shrink in lockstep as tabs
 *     are added — the strip never scrolls
 *   - density degradation: as the shared chip width falls, the popout button,
 *     then the close button (overlay-on-hover), then the title drop out
 *   - double-click / context-menu rename editing (the OWNER validates+saves;
 *     the strip only manages the input UI and emits the trimmed name)
 *   - per-chip popout + close buttons, close-all / close-others / close-right
 *     context-menu actions, the close-all badge button
 *   - per-chip tooltips (the primary way to read a heavily-truncated title)
 *
 * It is URL-first by construction: a chip click only emits `onSelect(key)` —
 * the consumer navigates; active state arrives back via the `activeKey` prop
 * (derived from the URL). The strip never writes any global state.
 */
import { Button } from '@src/components/ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@src/components/ui/context-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@src/components/ui/tooltip';
import { TabTipBodyContext, TabTooltipName } from '@src/components/tabs/TabTooltipName';
import { TabFavoriteStar } from '@src/components/tabs/TabFavoriteStar';
import type { FavoriteRef } from '@src/hooks/use-favorites';
import type { Annotation } from '@sdk';
import { notesByTarget, useAllNotes, type NoteTarget } from '@src/hooks/use-entity-notes';
import { TabNoteButton, TabNotesBadge, TabNotesPopover, type TabNotesView } from '@src/components/notes/TabNotes';
import { useLocaleInfo } from '@src/contexts/locale-context';
import { useIsAdvanced } from '@src/contexts/view-mode-context';
import { useCloseOnIframeFocus } from '@src/hooks/use-close-on-iframe-focus';
import { animateGlow } from '@src/lib/animate-glow';
import { chipSizing } from '@src/components/tabs/close-freeze';
import { useCloseFreeze } from '@src/components/tabs/use-close-freeze';
import { consumeTabHighlight, TAB_HIGHLIGHT_MS, useTabHighlights } from '@src/tabs/tab-highlight';
import { ExternalLink, X, type LucideIcon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { useLingui } from '@lingui/react/macro';

/**
 * The height of the tab line — the chip's own metrics (`border-t-2` + `py-1.5`
 * + a 20px `text-sm` line + the 1px bottom border), pinned so the strip is the
 * same height whether or not it has chips.
 *
 * Exported because the rail's first button must be exactly as tall: the two
 * start on the same line under the nav bar, so they have to END on it too, or
 * the app's top edge reads as two mismatched steps.
 */
export const TAB_LINE_HEIGHT_CLASS = 'h-[35px]';

/**
 * A chip's hover card. It is hoverable (it carries a copy button), and Radix
 * dismisses it on a press elsewhere in this document — but a press inside a
 * web app tab's iframe never reaches the document, so that closes it too.
 */
function TabChipTooltip({ trigger, children }: { trigger: React.ReactNode; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useCloseOnIframeFocus(open, close);

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>{trigger}</TooltipTrigger>
      {children}
    </Tooltip>
  );
}

/** One chip in the strip. Kind-agnostic: terminals, entity tabs, and the
 *  transient preview tab all render through this shape. */
export interface TabStripItem {
  /** Canonical tab key (TypeId string for entity tabs, pointer key for transient). */
  key: string;
  /** Display title. */
  title: string;
  /** Extra classes for the title text (e.g. blue for projectless/global tabs). */
  titleClassName?: string;
  /** Leading icon node (resolved by the owner — vendor override ?? type icon). */
  icon?: React.ReactNode;
  /** Extra inline markers after the icon (e.g. worktree badge). */
  badge?: React.ReactNode;
  isDisabled?: boolean;
  hasError?: boolean;
  statusReason?: string;
  /** Set on a GUEST tab — its content belongs to another project than the tab.
   *  The chip gets a green underline and this line in its tip. */
  guestNote?: string;
  /** Rename capability — present iff the tab has a target entity (Part 3 §3). */
  renameable?: boolean;
  /** What this chip DISPLAYS while standing in for something else — the ancestor
   *  chip showing the workspace child that fills the panel. `key` and `title`
   *  keep meaning "the row I am", so select/close/rename stay pointed at this
   *  tab; only the rendered icon and label defer. */
  standsFor?: { icon?: React.ReactNode; title: string };
  /** What bookmarking THIS tab means (`favoriteTargetForDock`) — present iff the
   *  tab is bookmarkable; the chip then leads with its own star. */
  favorite?: FavoriteRef | null;
  /** The entity this tab's sticky notes sit on — present iff the tab can carry
   *  notes; the chip then offers "Add a note" beside its star and a notes badge. */
  notes?: NoteTarget | null;
  /** Whether the chip can be closed (default true). A `closable: false` chip
   *  hides its X, is excluded from close-all/close-others/close-right, and shows
   *  no "Close" context item — for pinned fixtures like the vibe "Display" chip. */
  closable?: boolean;
  /** The tab tip's body in advanced mode, under the strip's own header (the
   *  full title); falls back to statusReason when disabled. */
  tooltip?: React.ReactNode;
  /** The tab tip's body in EVERY view mode, in place of `tooltip` — it owns its
   *  own padding. What this KIND of tab has to say (a session's state, a
   *  document's path); its own name line drops out under the header. */
  card?: React.ReactNode;
  /** data-testid for the chip. */
  testId?: string;
  /** Value for the data-provider attribute on the icon wrapper (test hook). */
  dataAttributes?: Record<string, string>;
  /** Extra per-chip context-menu entries (e.g. the transient preview tab's
   *  "Keep as tab" promotion — tab-management.md Part 3 §5). */
  contextMenuItems?: TabStripContextMenuItem[];
}

export interface TabStripContextMenuItem {
  label: string;
  shortcut?: string;
  onSelect: () => void;
  /** Optional leading icon (e.g. graph glyph for "Open Context"). */
  Icon?: LucideIcon;
  /** Renders the entry as a distinguished header row at the TOP of the menu
   *  (accented, above Rename, own separator) instead of a plain item — for
   *  navigation shortcuts like "Open Project" that aren't tab operations. */
  emphasized?: boolean;
}

export interface TabStripProps {
  items: TabStripItem[];
  activeKey: string;
  /** Chip click — consumer navigates (URL-first); never called for disabled chips. */
  onSelect: (key: string) => void;
  /** Close one tab (chip X / context menu / mod+W). Owner dispatches per-kind close. */
  onClose: (key: string) => void;
  /** Close many (close-all button / close-others / close-right). One batch. */
  onCloseMany?: (keys: string[]) => void;
  /** Rename commit with the trimmed new name. Only offered on renameable items. */
  onRename?: (key: string, name: string) => void;
  /** Pop the tab out (Part 3 §7/§8). */
  onPopout?: (key: string) => void;
  /** Extra "new tab" context-menu entries (owner-specific openers). */
  newTabMenuItems?: TabStripContextMenuItem[];
  /** Shortcut label for the Close entry (e.g. "Ctrl+W"). */
  closeShortcutLabel?: string;
  /** Trailing fixed node (opener toolbar) — sits after the tab row, never
   *  overlaps or shrinks; the chips share whatever width remains. */
  trailing?: React.ReactNode;
  /** Hide the aggregated close-all badge button. */
  hideCloseAllButton?: boolean;
  /** Drag-reorder (opt-in). During a drag the strip emits the predicted drop-gap
   *  anchors (chip keys; null = strip edge) so the owner can paint the optimistic
   *  order; on drop it emits the commit; a cancel (no move / escape) restores. */
  onReorderPreview?: (reorderKey: string, afterKey: string | null, beforeKey: string | null) => void;
  onReorderCommit?: (reorderKey: string, afterKey: string | null, beforeKey: string | null) => void;
  onReorderCancel?: () => void;
  testId?: string;
}

/** Strip-wide chip density, derived from the SHARED chip width (all chips are
 *  equal-width by construction, so one number describes every chip):
 *  `normal` = icon + title + in-flow hover buttons; `compact` = no popout,
 *  close is an overlay; `icon` = icon only, close overlay on hover/active. */
type ChipDensity = 'normal' | 'compact' | 'icon';

/** Preferred/max chip width — the SAME constant drives the flex layout (inline
 *  style) and the density thresholds, so the two can't drift apart. */
const CHIP_MAX_PX = 200;
/** Icon-only floor — chips never shrink below this; past it the row clips. */
const CHIP_MIN_PX = 40;
const COMPACT_BELOW_PX = 110;
const ICON_ONLY_BELOW_PX = 64;
/** Hover time before a tab's icon turns into its favorite star, so a plain tab click never lands on the star. 0 = immediate. */
const TAB_STAR_HOVER_DELAY_MS = 1500;
/** Visibility flips after the hover delay; leaving the tab flips it back at once. */
/** Shared empty list: a chip without notes gets the same array every render. */
const NO_NOTES: readonly Annotation[] = [];
const TAB_STAR_SWAP = 'transition-[visibility] duration-0 group-hover:[transition-delay:var(--tab-star-delay)]';

export const TabStrip: React.FC<TabStripProps> = ({
  items,
  activeKey,
  onSelect,
  onClose,
  onCloseMany,
  onRename,
  onPopout,
  newTabMenuItems,
  closeShortcutLabel,
  trailing,
  hideCloseAllButton,
  onReorderPreview,
  onReorderCommit,
  onReorderCancel,
  testId = 'terminal-tab-bar',
}) => {
  const { t } = useLingui();
  // One ordered list (backend-owned) — projectless tabs are inline, no section.
  const allVisibleItems = items;
  // `item.tooltip` is the Advanced/Dev body of a tab's tip; `item.card` shows in every mode.
  const isAdvanced = useIsAdvanced();
  const { dir } = useLocaleInfo();
  const tabContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const highlights = useTabHighlights();
  // Close-in-place: a mouse close holds every tab's width until the pointer leaves.
  const stripRef = useRef<HTMLDivElement>(null);
  const keyList = items.map((i) => i.key).join('\n');
  const keys = useMemo(() => (keyList ? keyList.split('\n') : []), [keyList]);
  const { frozenPx, leadPad, closeByMouse, onPointerLeave, onPointerEnter } = useCloseFreeze(keys, stripRef, CHIP_MAX_PX);

  useEffect(() => {
    for (const [key, token] of highlights) {
      const chip = tabRefs.current[key];
      if (!chip) continue;
      animateGlow(chip, TAB_HIGHLIGHT_MS);
      consumeTabHighlight(key, token);
    }
  }, [highlights, items]);

  // Sticky notes: ONE query for every chip, grouped by the entity each sits on,
  // and ONE popover (compose / list / note) anchored to the chip that opened it.
  const allNotes = useAllNotes(items.some((i) => i.notes));
  const notesFor = useMemo(() => notesByTarget(allNotes), [allNotes]);
  const [notesView, setNotesView] = useState<TabNotesView | null>(null);
  const notesItem = notesView ? items.find((i) => i.key === notesView.key) : undefined;

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');
  const renameInputRef = useRef<HTMLInputElement>(null);
  const shouldSelectRenameInputRef = useRef(false);

  // Density: one ResizeObserver on the row; all chips share one width
  // (row / count, clamped to the chip max), so a single strip-level state
  // keeps every chip's composition identical — no per-chip measurement.
  const [density, setDensity] = useState<ChipDensity>('normal');
  useEffect(() => {
    const container = tabContainerRef.current;
    if (!container) return;
    const compute = () => {
      // Unmeasurable container (0 width — e.g. jsdom, display:none host):
      // stay at 'normal' rather than degrading on a bogus measurement.
      const width = container.clientWidth;
      if (!width) return;
      // While frozen every tab keeps its width, so its density must not change either.
      const per = frozenPx ?? Math.min(width / (items.length || 1), CHIP_MAX_PX);
      setDensity(per >= COMPACT_BELOW_PX ? 'normal' : per >= ICON_ONLY_BELOW_PX ? 'compact' : 'icon');
    };
    compute();
    const observer = new ResizeObserver(compute);
    observer.observe(container);
    return () => observer.disconnect();
  }, [items.length, frozenPx]);

  // Keep the ACTIVE chip on screen.
  //
  // The Chrome model above shrinks every chip equally, so all of them stay
  // visible right down to the 40px floor. Past that floor the row CLIPS
  // (`overflow-hidden`), and because nothing ever scrolled it, a selected tab
  // beyond the fold was invisible AND unreachable — no scrollbar to recover it,
  // and the strip pinned at scrollLeft 0. Observed at 54 tabs: the active chip
  // sat at x=2260 in an 815px row.
  //
  // Scrolls only when the chip is actually out of view, so the common
  // (unclipped) case does no work and the row never jumps while you are reading
  // it. `scrollLeft` is set directly rather than via `scrollIntoView`, which
  // would also scroll ancestor containers and can yank the whole page.
  useEffect(() => {
    const container = tabContainerRef.current;
    const chip = activeKey ? tabRefs.current[activeKey] : null;
    if (!container || !chip) return;
    if (container.scrollWidth <= container.clientWidth) return; // not clipped

    // Measure the chip RELATIVE TO THE CONTAINER's content box. `offsetLeft` is
    // relative to `offsetParent`, which is not this container (it is not
    // positioned), so using it overshoots and pushes the chip off the other
    // edge — rect deltas are correct regardless of the offset parent.
    const containerRect = container.getBoundingClientRect();
    const chipRect = chip.getBoundingClientRect();
    const left = chipRect.left - containerRect.left + container.scrollLeft;
    const right = left + chipRect.width;
    const viewLeft = container.scrollLeft;
    const viewRight = viewLeft + container.clientWidth;
    // A little breathing room so the chip is not flush against the edge.
    const pad = 24;

    if (left < viewLeft + pad) {
      container.scrollLeft = Math.max(0, left - pad);
    } else if (right > viewRight - pad) {
      container.scrollLeft = right - container.clientWidth + pad;
    }
  }, [activeKey, items.length, density]);

  // Rename editing — the strip owns the input UI; the owner owns validation
  // and the save (entity rename / PTY `/rename` are kind strategies).
  const startRename = (key: string, currentName: string) => {
    shouldSelectRenameInputRef.current = true;
    setEditingKey(key);
    setEditingName(currentName);
  };

  useEffect(() => {
    if (!editingKey || !shouldSelectRenameInputRef.current) return;
    const input = renameInputRef.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(0, input.value.length);
  });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    shouldSelectRenameInputRef.current = false;
    setEditingName(e.target.value);
  };

  const handleNameBlur = () => {
    shouldSelectRenameInputRef.current = false;
    if (editingKey && editingName.trim()) {
      onRename?.(editingKey, editingName.trim());
    }
    setEditingKey(null);
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleNameBlur();
    } else if (e.key === 'Escape') {
      shouldSelectRenameInputRef.current = false;
      setEditingKey(null);
    } else {
      shouldSelectRenameInputRef.current = false;
    }
  };

  // Pinned (closable === false) chips are never batch-closed.
  const closableKeys = new Set(items.filter((i) => i.closable === false).map((i) => i.key));
  const closeMany = (keys: string[]) => {
    const closeable = keys.filter((k) => !closableKeys.has(k));
    if (closeable.length === 0) return;
    if (onCloseMany) onCloseMany(closeable);
    else closeable.forEach((k) => onClose(k));
  };

  // One context-menu group: the entries followed by a separator. Shared by the
  // per-chip extra entries (item.contextMenuItems) and the owner's "new tab"
  // entries (newTabMenuItems) — identical rendering, different source.
  const renderMenuGroup = (entries: TabStripContextMenuItem[] | undefined) => {
    if (!entries || entries.length === 0) return null;
    return (
      <>
        {entries.map((entry) => (
          <ContextMenuItem
            key={entry.label}
            onSelect={entry.onSelect}
            className={entry.emphasized ? 'bg-accent/50 font-medium text-foreground focus:bg-accent' : undefined}
          >
            {entry.Icon && <entry.Icon className={`me-2 h-4 w-4${entry.emphasized ? 'text-primary' : ''}`} />}
            {entry.label}
            {entry.shortcut && <span className="ms-auto ps-4 text-xs text-muted-foreground">{entry.shortcut}</span>}
          </ContextMenuItem>
        ))}
        <ContextMenuSeparator />
      </>
    );
  };

  // ── Native pointer-drag reorder (no dnd dependency) ─────────────────────────
  // The strip never reorders its own array: it emits the predicted drop-gap
  // anchors and the owner repaints from the backend-owned store (so the dragged
  // chip flows under the cursor). Commit on drop; a no-move release is just a
  // click (suppressed so it doesn't navigate after a drag).
  const [dragKey, setDragKey] = useState<string | null>(null);
  const movedRef = useRef(false);
  const justDraggedRef = useRef(false);

  const anchorsAt = useCallback(
    (clientX: number, draggedKey: string): { after: string | null; before: string | null } => {
      // Single pass over the (non-dragged) chips: the gap is between the last
      // chip the cursor has passed (`after`) and the first one still ahead
      // (`before`) — "passed" follows the reading direction.
      let after: string | null = null;
      let before: string | null = null;
      for (const it of items) {
        if (it.key === draggedKey) continue;
        const el = tabRefs.current[it.key];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        const mid = r.left + r.width / 2;
        if (dir === 'rtl' ? clientX <= mid : clientX >= mid) {
          after = it.key;
        } else {
          before = it.key;
          break;
        }
      }
      return { after, before };
    },
    [items, dir],
  );

  const startDrag = useCallback(
    (e: React.PointerEvent, key: string, isDisabled: boolean) => {
      if (!onReorderPreview || isDisabled || editingKey) return;
      if ((e.target as HTMLElement).closest('button,input')) return;
      const startX = e.clientX;
      movedRef.current = false;
      const onMove = (ev: PointerEvent) => {
        if (!movedRef.current && Math.abs(ev.clientX - startX) < 5) return;
        if (!movedRef.current) {
          movedRef.current = true;
          setDragKey(key);
        }
        const { after, before } = anchorsAt(ev.clientX, key);
        onReorderPreview(key, after, before);
      };
      const onUp = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (movedRef.current) {
          const { after, before } = anchorsAt(ev.clientX, key);
          onReorderCommit?.(key, after, before);
          justDraggedRef.current = true;
          setTimeout(() => (justDraggedRef.current = false), 0);
        }
        setDragKey(null);
        movedRef.current = false;
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [onReorderPreview, onReorderCommit, anchorsAt, editingKey],
  );

  const activeIndex = allVisibleItems.findIndex((i) => i.key === activeKey);

  // Popout + close live in an absolute overlay at EVERY density (the Chrome
  // model): in-flow buttons would reserve ~48px of every chip even while
  // invisible and crush the title. The overlay is backed by the chip surface
  // color so it reads over truncated text; close is always visible on the
  // active chip, hover-revealed on inactive ones; popout is hover-only, sits
  // just before close, and drops out below normal density.
  const renderButtonOverlay = (
    key: string,
    isActive: boolean,
    isDisabled: boolean,
    closable: boolean,
    showPopout: boolean,
  ) => (
    <div
      className={`absolute end-0.5 top-1/2 z-10 flex -translate-y-1/2 items-center gap-0.5 rounded transition-opacity ${
        isActive ? 'bg-background opacity-100' : 'bg-muted opacity-0 group-hover:opacity-100'
      }`}
    >
      {showPopout && onPopout && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onPopout(key);
          }}
          disabled={isDisabled}
          className={`rounded p-0.5 hover:bg-muted-foreground/20 ${isActive ? 'opacity-0 group-hover:opacity-100' : ''}`}
          aria-label={t`Open in external browser`}
          title={t`Open in external browser`}
          data-testid={`tab-open-external-${key}`}
        >
          <ExternalLink className="h-3 w-3" />
        </button>
      )}
      {closable && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            const chip = tabRefs.current[key];
            if (chip) closeByMouse(key, chip);
            onClose(key);
          }}
          disabled={isDisabled}
          className="rounded p-0.5 hover:bg-destructive/20"
          aria-label={t`Close tab`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </div>
  );

  // One chip in the single ordered row. `list` scopes close-others / close-right.
  const renderChip = (item: TabStripItem, index: number, list: TabStripItem[]) => {
    const { key } = item;
    const isActive = activeKey === key;
    const isDisabled = !!item.isDisabled;
    const hasError = !!item.hasError;
    const isEditing = editingKey === key;
    const displayTitle = item.standsFor?.title ?? item.title;
    const iconOnly = density === 'icon' && !isEditing;
    const closable = item.closable !== false;
    const showPopout = density === 'normal' && !!onPopout;
    // On the active tab only, the bookmark star takes the icon's place on hover.
    const showStar = !!item.favorite && !isEditing && isActive;
    const noteTarget = !isEditing ? item.notes : null;
    const chipNotes = (noteTarget && notesFor.get(noteTarget.typeId.toString())) || NO_NOTES;

    // Chrome model: equal flex share for every chip — all tabs stay visible and
    // shrink in lockstep; the strip never scrolls. Inline style (not Tailwind
    // literals) so the widths come from the same constants the density math
    // uses. An editing chip is temporarily pinned to the full preferred width
    // so the rename input is usable; neighbors re-equalize around it.
    // While frozen (close-in-place) every chip keeps one fixed width.
    const sizing = chipSizing(frozenPx, isEditing, { maxPx: CHIP_MAX_PX, minPx: CHIP_MIN_PX });

    const tabContent = (
      <div
        ref={(node) => {
          tabRefs.current[key] = node;
        }}
        style={sizing}
        // `border-t-2` on EVERY state, colored only when active: the accent has
        // to follow the `rounded-t-lg` curve, and a border does that natively.
        // It used to be an absolutely-positioned 2px bar, which `overflow-hidden`
        // clipped into a straight chord across the corner curve — the ends came
        // out visibly pointed. Uniform on all states so activation never changes
        // a chip's metrics.
        className={`group relative flex select-none items-center overflow-hidden rounded-t-lg border border-t-2 py-1.5 transition-colors motion-safe:transition-[color,background-color,border-color,flex-basis,min-width,max-width] ${
          iconOnly ? 'justify-center gap-0 px-1' : 'gap-2 px-3'
        } ${
          isDisabled
            ? 'cursor-not-allowed border-transparent bg-transparent text-muted-foreground/50'
            : hasError
              ? 'cursor-pointer border-destructive/40 bg-destructive/10 text-destructive hover:bg-destructive/15'
              : isActive
                ? // Active tab shares the body background and opens its bottom edge
                  // (-mb-px over the baseline) so it reads as one surface with the
                  // content below — a folder-tab continuum lifted off the muted band.
                  // Its top border IS the accent, so it hugs the rounded corners.
                  'z-10 -mb-px cursor-pointer border-border border-b-transparent border-t-primary bg-background text-foreground shadow-sm'
                : // Inactive tabs are flat on the band (Chrome-style); the
                  // transparent border keeps their box metrics identical to the
                  // active chip so activation never shifts neighbors.
                  'cursor-pointer border-transparent text-muted-foreground hover:bg-foreground/5 hover:text-foreground'
        } ${dragKey === key ? 'opacity-60' : ''}`}
        onPointerDown={(e) => startDrag(e, key, isDisabled)}
        onClick={() => {
          if (isDisabled) return;
          if (justDraggedRef.current) return; // a drag just ended — don't navigate
          onSelect(key);
        }}
        data-testid={item.testId}
        data-terminal-target={key}
        {...(item.dataAttributes ?? {})}
        data-active={isActive ? 'true' : undefined}
      >
        <span
          className="relative inline-flex shrink-0 items-center justify-center"
          style={{ '--tab-star-delay': `${TAB_STAR_HOVER_DELAY_MS}ms` } as React.CSSProperties}
        >
          <span className={`inline-flex ${showStar ? `${TAB_STAR_SWAP} group-hover:invisible` : ''}`}>
            {item.standsFor?.icon ?? item.icon}
          </span>
          {item.favorite && showStar && (
            <span
              className={`invisible absolute inset-0 flex items-center justify-center ${TAB_STAR_SWAP} group-hover:visible`}
            >
              <TabFavoriteStar favorite={item.favorite} chip={() => tabRefs.current[key] ?? null} />
            </span>
          )}
          {noteTarget && isActive && !iconOnly && (
            // Beside the star, after the same hover delay; it floats over the
            // title's first letters rather than reserving width on every chip.
            <span
              className={`invisible absolute start-full ms-1 flex items-center rounded bg-muted px-0.5 ${TAB_STAR_SWAP} group-hover:visible`}
            >
              <TabNoteButton onClick={() => setNotesView({ key, mode: 'compose' })} />
            </span>
          )}
        </span>
        {!iconOnly && item.badge}
        {isEditing ? (
          <input
            ref={renameInputRef}
            type="text"
            value={editingName}
            onChange={handleNameChange}
            onBlur={handleNameBlur}
            onKeyDown={handleNameKeyDown}
            className="w-full min-w-0 rounded border border-border bg-background px-1 py-0 text-sm focus:outline-none focus:ring-1 focus:ring-primary"
            autoFocus
            onFocus={(e) => {
              if (shouldSelectRenameInputRef.current) {
                e.currentTarget.setSelectionRange(0, e.currentTarget.value.length);
              }
            }}
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          !iconOnly && (
            <span
              // The active chip's close button is persistent (overlay) — keep
              // the title clear of it; inactive titles reclaim the full width.
              className={`min-w-0 flex-1 truncate text-sm font-medium ${isActive && chipNotes.length === 0 ? 'pe-4' : ''} ${item.titleClassName ?? ''}`}
              onDoubleClick={(e) => {
                if (!item.renameable) return;
                e.stopPropagation();
                startRename(key, item.title);
              }}
            >
              {displayTitle}
            </span>
          )
        )}
        {!iconOnly && chipNotes.length > 0 && (
          <span className={`inline-flex shrink-0 ${isActive ? 'pe-4' : ''}`}>
            <TabNotesBadge notes={chipNotes} onClick={() => setNotesView({ key, mode: 'list' })} />
          </span>
        )}

        {!isEditing && renderButtonOverlay(key, isActive, isDisabled, closable, showPopout)}
        {item.guestNote && (
          <span
            aria-hidden
            data-testid="tab-guest-underline"
            className="pointer-events-none absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-emerald-500"
          />
        )}
      </div>
    );

    // Separator between chips (Chrome-style): hidden next to the active chip so
    // the raised tab reads as a single surface. Kept as a transparent element
    // (not removed) so chip geometry is stable; shown everywhere during a drag
    // to avoid flicker while the active index moves under the preview.
    // A FOREGROUND tint, not `bg-border`: in dark the border token IS the band's
    // colour (both hsl 0 0% 14.9%), so the separator was drawn and invisible.
    const hideSeparator = !dragKey && (index - 1 === activeIndex || index === activeIndex);
    // The tab tip: the strip's own header (the full title, which the chip may
    // cut) over the body this kind of tab defines.
    const tipBody =
      item.card ??
      (isAdvanced && item.tooltip ? (
        <div className="p-2.5">{item.tooltip}</div>
      ) : isDisabled && item.statusReason ? (
        <p className="px-3 py-1.5">{item.statusReason}</p>
      ) : null);

    const separator =
      index > 0 ? (
        <div
          aria-hidden
          className={`h-4 w-px shrink-0 self-center ${hideSeparator ? 'bg-transparent' : 'bg-foreground/20'}`}
        />
      ) : null;

    return (
      <React.Fragment key={key}>
        {separator}
        <ContextMenu>
          <TabChipTooltip trigger={<ContextMenuTrigger asChild>{tabContent}</ContextMenuTrigger>}>
            {(displayTitle || tipBody) && (
              <TooltipContent side="bottom" className="p-0">
                {displayTitle && (
                  <div className="px-3 py-1.5">
                    <TabTooltipName name={displayTitle} className={tipBody ? undefined : 'font-normal'} />
                    {item.guestNote && (
                      <p className="text-[11px] text-emerald-500" data-testid="tab-guest-note">
                        {item.guestNote}
                      </p>
                    )}
                  </div>
                )}
                {noteTarget && (
                  <div className="flex items-center gap-1.5 border-t border-border px-3 py-1.5 text-[11px] text-muted-foreground">
                    <span className="flex-1">
                      {chipNotes.length > 0 ? t`${chipNotes.length} notes` : t`No notes yet`}
                    </span>
                    <button
                      type="button"
                      className="rounded px-1.5 py-0.5 text-foreground hover:bg-muted"
                      onClick={() => setNotesView({ key, mode: chipNotes.length ? 'list' : 'compose' })}
                    >
                      {chipNotes.length > 0 ? t`Show notes` : t`+ Add note`}
                    </button>
                  </div>
                )}
                {/* A body may turn out empty once it has loaded: no divider then. */}
                {tipBody && (
                  <div className="border-t border-border empty:hidden">
                    <TabTipBodyContext.Provider value>{tipBody}</TabTipBodyContext.Provider>
                  </div>
                )}
              </TooltipContent>
            )}
          </TabChipTooltip>
          <ContextMenuContent>
            {/* Emphasized shortcuts (e.g. "Open Project") sit above the tab
                operations as an accented header group — they navigate, they
                don't mutate the tab, so they must not read as one more entry. */}
            {renderMenuGroup(item.contextMenuItems?.filter((e) => e.emphasized))}
            {item.renameable && (
              <>
                <ContextMenuItem onSelect={() => startRename(key, item.title)}>
                  <Trans>Rename</Trans>
                </ContextMenuItem>
                <ContextMenuSeparator />
              </>
            )}
            {renderMenuGroup(item.contextMenuItems?.filter((e) => !e.emphasized))}
            {renderMenuGroup(newTabMenuItems)}
            {item.closable !== false && (
              <ContextMenuItem onSelect={() => onClose(key)}>
                <Trans>Close</Trans>{' '}
                {closeShortcutLabel && (
                  <span className="ms-auto ps-4 text-xs text-muted-foreground">{closeShortcutLabel}</span>
                )}
              </ContextMenuItem>
            )}
            <ContextMenuItem onSelect={() => closeMany(allVisibleItems.map((i) => i.key))}>
              <Trans>Close All</Trans>
            </ContextMenuItem>
            <ContextMenuItem
              onSelect={() => closeMany(list.filter((i) => i.key !== key).map((i) => i.key))}
              disabled={list.length <= 1}
            >
              <Trans>Close All But This</Trans>
            </ContextMenuItem>
            <ContextMenuItem
              onSelect={() => closeMany(list.slice(index + 1).map((i) => i.key))}
              disabled={index >= list.length - 1}
            >
              <Trans>Close to the Right</Trans>
            </ContextMenuItem>
          </ContextMenuContent>
        </ContextMenu>
      </React.Fragment>
    );
  };

  return (
    // The strip is a muted BAND (theme tokens only — muted contrasts with
    // background in every theme) that the active chip lifts out of into the
    // body. min-w-0/max-w-full: the strip must never size its host to content.
    // One tooltip provider for the whole strip: the first tip waits, and sliding
    // on to the next chip shows its tip at once.
    <TooltipProvider delayDuration={600}>
      {notesView && notesItem?.notes && (
        <TabNotesPopover
          view={notesView}
          target={notesItem.notes}
          notes={notesFor.get(notesItem.notes.typeId.toString()) ?? NO_NOTES}
          anchor={tabRefs.current[notesView.key] ?? null}
          onView={setNotesView}
          onClose={() => setNotesView(null)}
        />
      )}
      <div
        className={`flex min-w-0 max-w-full items-end bg-muted ${TAB_LINE_HEIGHT_CLASS}`}
        ref={stripRef}
        data-testid={testId}
        onPointerLeave={onPointerLeave}
        onPointerEnter={onPointerEnter}
      >
        {/* Tab row — chips share this width equally and are ALL always visible
            (Chrome model). overflow-hidden only matters past the 40px/chip floor
            (~25+ tabs at 1000px), where the row clips instead of scrolling. */}
        <div
          ref={tabContainerRef}
          data-testid="terminal-tabs-row"
          data-frozen={frozenPx !== null ? 'true' : undefined}
          className="flex min-w-0 flex-1 items-end overflow-hidden pe-1"
        >
          {/* Closing tabs from the end grows this spacer, so the tabs shift toward the cursor. */}
          {leadPad > 0 && (
            <div
              aria-hidden
              data-testid="tab-strip-lead-pad"
              className="shrink-0"
              style={{ flex: `0 0 ${leadPad}px` }}
            />
          )}
          {items.map((item, index) => renderChip(item, index, items))}
        </div>

        {/* Opener toolbar — a fixed sibling (never overlaps or shrinks); the
            chips absorb all width pressure. */}
        {trailing && <div className="flex shrink-0 items-center self-stretch">{trailing}</div>}

        {/* Close All — the strip's LAST control, at a clear distance from the
            openers so a click aimed at "+" can't land on it. Same shape as "+"
            (a 7×7 rounded bordered button), its own colour. Shown at 2+ tabs; the
            count badge hints at the destructive scope before clicking. */}
        {!hideCloseAllButton && allVisibleItems.length >= 2 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="me-1 ms-3 h-7 shrink-0 gap-1 rounded-md border-border bg-background px-1.5 text-foreground shadow-sm hover:border-destructive/60 hover:bg-destructive/10 hover:text-destructive"
                onClick={() => closeMany(allVisibleItems.map((i) => i.key))}
                aria-label={t`Close all ${allVisibleItems.length} tabs`}
                data-testid="close-all-tabs-button"
              >
                <X className="h-3.5 w-3.5" />
                <span className="inline-flex h-4 min-w-[1.125rem] items-center justify-center rounded-full bg-foreground/10 px-1 text-[10px] font-semibold tabular-nums leading-none text-foreground">
                  {allVisibleItems.length}
                </span>
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <Trans>Close all {allVisibleItems.length} tabs</Trans>
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
};

export default TabStrip;
